import React, { useState, useEffect, useRef, useCallback } from 'react';
import ContactForm from './ContactForm';
import { CONTACT_EMAIL } from '../site';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ID_PREFIX = 'modal';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  subject?: string;
}

const ContactSection: React.FC<ContactModalProps> = ({ isOpen, onClose, subject }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Stable identity, or the form's busy effect would fire on every parent render.
  const handleBusyChange = useCallback((busy: boolean) => setIsSubmitting(busy), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Kept separate from the dismissal listeners above: onClose is a new function on
  // every parent render, and re-running focus restoration would yank focus mid-form.
  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.getElementById(`${ID_PREFIX}-name`)?.focus();

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modalContentRef.current) return;

      const focusable = Array.from(
        modalContentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(element => element.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !modalContentRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-950/80 p-4 transition-opacity duration-300 ease-in-out">
      <div className="flex min-h-full items-center justify-center">
        <div
          ref={modalContentRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-modal-title"
          className="relative w-full max-w-lg transform rounded-lg bg-gray-900 p-6 shadow-xl transition-all duration-300 ease-in-out scale-100 sm:p-8 md:p-12"
        >
          <button
            onClick={onClose}
            className="absolute top-2 right-2 inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-400 transition-colors hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-accent-400"
            aria-label="Close contact form"
            disabled={isSubmitting}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="mb-8 pr-10">
            <h2
              id="contact-modal-title"
              className="font-display text-display-sm font-bold text-white sm:text-display-md"
            >
              Tell us what you need
            </h2>
            <p className="mt-3 text-gray-300">
              We read every message and reply ourselves. Prefer email? Write to{' '}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="rounded text-accent-400 underline transition-colors duration-150 hover:text-accent-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </div>

          <ContactForm
            idPrefix={ID_PREFIX}
            subject={subject}
            onBusyChange={handleBusyChange}
            successAction={
              <button
                onClick={onClose}
                className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-gray-600 px-5 py-2.5 text-sm font-medium text-gray-300 transition-colors duration-150 hover:border-gray-400 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
              >
                Close
              </button>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
